import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Close } from '@liveauctioneers/hammer-ui-core/icons';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { ExternalRouteButton } from '@/components/RouteButton/RouteButton';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getCookie, setCookie } from '@/redux/modules/cookies';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { getMobileBrowserOS } from '../../redux/modules/browser';

type Props = {
    className?: string;
};

const COOKIE_MAX_AGE = 86400 * 30; // 30 days
const COOKIE_NAME = 'dismissableMobileBannerAlt';
const COOKIE_BODY = 'dismissed';

export const DismissableGetMobileBannerAlt = ({ className }: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const { pathname } = useLocation();
    const mobileBrowserOS = useAppSelector(getMobileBrowserOS);
    const dismissed = useAppSelector((state) => getCookie(state, COOKIE_NAME)) === COOKIE_BODY;
    const isCheckoutPage = pathname.includes('/checkout/');
    const biddingConsole = pathname.includes('/console/');

    const setCookieChecked = useCallback((): void => {
        dispatch(
            setCookie({
                maxAge: COOKIE_MAX_AGE,
                name: COOKIE_NAME,
                value: COOKIE_BODY,
            })
        );
    }, [dispatch]);

    if (biddingConsole || isCheckoutPage || mobileBrowserOS === '' || dismissed) {
        return null;
    }

    const isIOS = mobileBrowserOS === 'ios';
    const navTo = isIOS
        ? 'https://itunes.apple.com/us/app/live-auctions/id321243082'
        : 'https://play.google.com/store/apps/details?id=com.liveauctioneers.and';

    return (
        <div
            className={cn(
                'relative flex h-[120px] items-center justify-around bg-surface-100 pr-[21px] pl-2xs',
                className
            )}
            id="mobileBannerAlt"
        >
            {isIOS ? (
                <img
                    alt="iphone"
                    className="max-h-[110px] self-end"
                    src="https://p1.liveauctioneers.com/dist/images/iPhoneXs_Download_Home.png"
                />
            ) : (
                <img
                    alt="androidPhone"
                    className="max-h-[110px] self-end"
                    src="https://p1.liveauctioneers.com/dist/images/Pixel3_Download_Home.png"
                />
            )}
            <div className="flex flex-col items-center pr-xs pb-3xs [&_a]:no-underline">
                <div className="mb-xs max-w-[210px] text-center font-secondary text-sm leading-[18px] tracking-[0.5px] text-text-primary">
                    {formatMessage(['pro_tip_track_all_treasures'])}
                </div>
                <ExternalRouteButton
                    className="!h-[26px] !w-[76px] !bg-background-primary !p-0 !text-xs"
                    rel="nofollow noopener"
                    style="primary"
                    target="_blank"
                    to={navTo}
                    variant="outlined"
                >
                    {formatMessage(['get_app'])}
                </ExternalRouteButton>
            </div>
            <IconButton
                className="absolute top-xs right-xs z-10"
                icon={Close}
                iconStyle="bold"
                onClick={setCookieChecked}
                size="xs"
                variant="transparent"
            />
        </div>
    );
};
